import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Button, TextField, CircularProgress,
  Grid, Tab, Tabs, LinearProgress,
} from '@mui/material';
import { TrendingUp, Print } from '@mui/icons-material';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useReactToPrint } from 'react-to-print';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import api from '../../api/axios';

const fmt = (n) => new Intl.NumberFormat('en-PK').format(Math.round(n || 0));
const COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

function MarginBar({ pct }) {
  const color = pct >= 30 ? 'success' : pct >= 15 ? 'warning' : 'error';
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <LinearProgress variant="determinate" value={Math.min(100, Math.max(0, pct))}
        sx={{ flex: 1, height: 8, borderRadius: 4 }} color={color} />
      <Typography variant="caption" fontWeight={700} color={`${color}.main`}>
        {pct?.toFixed(1)}%
      </Typography>
    </Box>
  );
}

export default function MarginsReportPage() {
  const [tab, setTab] = useState(0);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().split('T')[0]; });
  const [to, setTo] = useState(new Date().toISOString().split('T')[0]);
  const printRef = useRef();
  const handlePrint = useReactToPrint({ contentRef: printRef });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [marginRes, catRes] = await Promise.all([
        api.get('/reports/margins', { params: { from, to, limit: 100 } }),
        api.get('/reports/by-category', { params: { from, to } }),
      ]);
      setProducts(marginRes.data.data.rows || []);
      setSummary(marginRes.data.data.summary);
      setCategories(catRes.data.data || []);
    } finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TrendingUp color="primary" />
          <Typography variant="h5" fontWeight={700}>Profit Margins & Category Sales / منافع رپورٹ</Typography>
        </Box>
        <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>Print</Button>
      </Box>

      {/* Date filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ display: 'flex', gap: 2, pb: '16px !important', flexWrap: 'wrap' }}>
          <TextField label="From" type="date" size="small" value={from}
            onChange={e => setFrom(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="To" type="date" size="small" value={to}
            onChange={e => setTo(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="contained" onClick={load} disabled={loading}>
            {loading ? <CircularProgress size={18} /> : 'Generate'}
          </Button>
          {[
            { label: 'This Month', fn: () => { const d = new Date(); d.setDate(1); setFrom(d.toISOString().split('T')[0]); setTo(new Date().toISOString().split('T')[0]); } },
            { label: 'Last 3 Months', fn: () => { const d = new Date(); d.setMonth(d.getMonth() - 3); setFrom(d.toISOString().split('T')[0]); setTo(new Date().toISOString().split('T')[0]); } },
            { label: 'This Year', fn: () => { setFrom(`${new Date().getFullYear()}-01-01`); setTo(new Date().toISOString().split('T')[0]); } },
          ].map(r => <Button key={r.label} size="small" variant="outlined" onClick={r.fn}>{r.label}</Button>)}
        </CardContent>
      </Card>

      {/* Summary KPIs */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {[
            { label: 'Total Revenue', value: `₨ ${fmt(summary.total_revenue)}`, color: 'primary.main' },
            { label: 'Total COGS', value: `₨ ${fmt(summary.total_cogs)}`, color: 'warning.main' },
            { label: 'Gross Profit', value: `₨ ${fmt(summary.total_profit)}`, color: 'success.main' },
            { label: 'Avg Margin', value: `${summary.avg_margin}%`, color: +summary.avg_margin >= 20 ? 'success.main' : 'error.main' },
          ].map((kpi, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Card><CardContent sx={{ pb: '16px !important' }}>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                <Typography variant="h6" fontWeight={700} color={kpi.color}>{kpi.value}</Typography>
              </CardContent></Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Box ref={printRef}>
        <style>{`@media print { @page { size: A4 landscape; margin: 10mm; } }`}</style>

        <Card>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}>
            <Tab label="Product Margins" />
            <Tab label="Sales by Category" />
          </Tabs>

          {tab === 0 && (
            <>
              {/* Top 10 chart */}
              {products.slice(0, 10).length > 0 && (
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle2" fontWeight={700} gutterBottom>Top 10 by Gross Profit</Typography>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={products.slice(0, 10).map(p => ({ name: p.name.slice(0, 12), profit: +p.gross_profit, margin: +p.margin_pct }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={v => `₨${(v / 1000).toFixed(0)}k`} />
                      <Tooltip formatter={(v, n) => [n === 'profit' ? `₨ ${fmt(v)}` : `${v}%`, n === 'profit' ? 'Profit' : 'Margin']} />
                      <Bar dataKey="profit" fill="#22c55e" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}

              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Units Sold</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>COGS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Gross Profit</TableCell>
                    <TableCell sx={{ fontWeight: 700, width: 160 }}>Margin %</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>}
                  {!loading && products.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>No sales data for this period</TableCell></TableRow>}
                  {products.map((p, i) => (
                    <TableRow key={p.id} hover sx={{ bgcolor: p.gross_profit < 0 ? '#fef2f2' : 'inherit' }}>
                      <TableCell sx={{ color: 'text.secondary' }}>{i + 1}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{p.name}</Typography>
                        {p.sku && <Typography variant="caption" color="text.secondary" fontFamily="monospace">{p.sku}</Typography>}
                      </TableCell>
                      <TableCell>{p.category || '—'}</TableCell>
                      <TableCell align="right">{fmt(p.units_sold)}</TableCell>
                      <TableCell align="right">₨ {fmt(p.revenue)}</TableCell>
                      <TableCell align="right"><Typography color="warning.main">₨ {fmt(p.cogs)}</Typography></TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={700} color={p.gross_profit >= 0 ? 'success.main' : 'error.main'}>
                          ₨ {fmt(p.gross_profit)}
                        </Typography>
                      </TableCell>
                      <TableCell><MarginBar pct={+p.margin_pct} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}

          {tab === 1 && (
            <>
              {/* Pie chart */}
              {categories.length > 0 && (
                <Box sx={{ p: 2, display: 'flex', gap: 2, flexWrap: 'wrap', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ flex: '1 1 300px' }}>
                    <Typography variant="subtitle2" fontWeight={700} gutterBottom>Revenue by Category</Typography>
                    <ResponsiveContainer width="100%" height={220}>
                      <PieChart>
                        <Pie data={categories} dataKey="revenue" nameKey="category" cx="50%" cy="50%" outerRadius={80} label={({ category, percent }) => `${category} ${(percent * 100).toFixed(0)}%`}>
                          {categories.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => `₨ ${fmt(v)}`} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              )}

              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Invoices</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Units Sold</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Revenue</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>COGS</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>Profit</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Revenue Share</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>}
                  {!loading && categories.length === 0 && <TableRow><TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>No data</TableCell></TableRow>}
                  {(() => {
                    const totalRev = categories.reduce((s, c) => s + +c.revenue, 0);
                    return categories.map((c, i) => (
                      <TableRow key={i} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: COLORS[i % COLORS.length] }} />
                            <Typography fontWeight={600}>{c.category}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">{c.invoices}</TableCell>
                        <TableCell align="right">{fmt(c.units_sold)}</TableCell>
                        <TableCell align="right"><Typography fontWeight={700}>₨ {fmt(c.revenue)}</Typography></TableCell>
                        <TableCell align="right"><Typography color="warning.main">₨ {fmt(c.cogs)}</Typography></TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color={c.profit >= 0 ? 'success.main' : 'error.main'}>
                            ₨ {fmt(c.profit)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ width: 160 }}>
                          <MarginBar pct={totalRev > 0 ? (+c.revenue / totalRev) * 100 : 0} />
                        </TableCell>
                      </TableRow>
                    ));
                  })()}
                </TableBody>
              </Table>
            </>
          )}
        </Card>
      </Box>
    </Box>
  );
}
